/**
 * A WebSocket that comes back after the connection drops.
 *
 * Why this exists: the awl/exam sockets are idle by design — they only carry
 * traffic when a worklist or exam actually changes. Behind the ALB (default
 * idle timeout 60s) an idle socket is closed by the load balancer, and the
 * previous code registered only `onmessage`, so the socket died silently and
 * never came back: no error, no console message, the worklist simply stopped
 * updating until the user navigated. The server also pings every 30s; this is
 * the client half of keeping these connections alive.
 *
 * Reconnects use exponential backoff with jitter — the sites view opens one
 * socket per partner clinic, and without jitter they would all retry in
 * lockstep after a drop. Attempts are capped so that a socket rejected for
 * auth reasons (an expired session) backs off and stops rather than hammering.
 */
export class ReconnectingSocket {
  private ws: WebSocket | null = null;
  private closedByCaller = false;
  private attempt = 0;
  private timer: any = null;

  private static readonly BASE_DELAY_MS = 1000;
  private static readonly MAX_DELAY_MS = 30000;
  private static readonly MAX_ATTEMPTS = 8;

  constructor(
    private path: string,
    private onMessage: (ev: MessageEvent) => void,
  ) {
    document.addEventListener("visibilitychange", this.revive);
    window.addEventListener("online", this.revive);
    this.connect();
  }

  /**
   * Attempts are capped so a socket rejected for auth reasons backs off and
   * stops rather than hammering. But "gave up" must not mean "gave up forever":
   * a laptop closed overnight exhausts the attempts while nothing is watching,
   * and the page then looks healthy with a dead socket and a worklist that
   * silently stops updating — the same failure the keepalive was added to fix.
   *
   * Coming back to the tab, or regaining the network, is a strong signal that
   * conditions changed. Reset and try again.
   */
  private revive = () => {
    if (this.closedByCaller) return;
    if (document.visibilityState !== "visible") return;
    // Only act on a fully closed socket. CLOSING still has an onclose pending
    // that will schedule its own retry — connecting here as well would leave
    // two live sockets racing.
    if (this.readyState !== WebSocket.CLOSED) return;
    this.attempt = 0;
    clearTimeout(this.timer);
    this.connect();
  };

  private connect() {
    // Detach the previous socket's handlers before replacing it. Without this a
    // late onclose from the old socket can schedule another reconnect on top of
    // the one just started.
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
    }
    try {
      this.ws = new WebSocket(this.path);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws.onopen = () => { this.attempt = 0; };
    this.ws.onmessage = (ev) => this.onMessage(ev);
    this.ws.onclose = () => { if (!this.closedByCaller) this.scheduleReconnect(); };
    // onerror is always followed by onclose, so let onclose drive the retry.
    this.ws.onerror = () => { };
  }

  private scheduleReconnect() {
    if (this.closedByCaller || this.attempt >= ReconnectingSocket.MAX_ATTEMPTS) return;
    const backoff = Math.min(
      ReconnectingSocket.BASE_DELAY_MS * Math.pow(2, this.attempt),
      ReconnectingSocket.MAX_DELAY_MS,
    );
    this.attempt++;
    const jittered = backoff * (0.5 + Math.random() / 2);
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.connect(), jittered);
  }

  get readyState(): number {
    return this.ws ? this.ws.readyState : WebSocket.CLOSED;
  }

  close() {
    this.closedByCaller = true;
    clearTimeout(this.timer);
    document.removeEventListener("visibilitychange", this.revive);
    window.removeEventListener("online", this.revive);
    try { this.ws?.close(1000, "Work complete"); } catch { }
  }
}
